/**
 * "Declare manually open" — the operator-facing half of the door
 * manual-open override (W8b).
 *
 * THE PROBLEM IT ANSWERS. The pinned RMF's DoorOpen phase has NO
 * timeout: it completes on `door_states` reporting MODE_OPEN plus a
 * supervisor session, and delay merely accrues. A robot whose route
 * crosses a door that never reports OPEN therefore waits FOREVER and
 * nothing alerts — which is exactly what `L4_SLL_Door` did at
 * sg-nomad-01 when its MEGAZO adapter could not log in to its vendor
 * cloud and published no state at all.
 *
 * WHAT THE PRESS ACTUALLY DOES, and why the dialog says it in those
 * words: it writes a record in the site's lifecycle broker;
 * `rmf-door-state-mux` sees that record and SUPPRESSES the door
 * adapter's raw states, synthesizing MODE_OPEN on its own 1 s timer.
 * So RMF believes the door is open and drives a robot through it —
 * whatever the door is really doing. The mux works with the adapter
 * dead, which is the whole point, and it also means nothing downstream
 * can contradict a false declaration. The robot's own obstacle
 * avoidance still stands, so the expected failure of a wrong
 * declaration is a stall in the doorway rather than a collision; the
 * attestation is the control that matters.
 *
 * TTL-ONLY IN v1 — there is no auto-revoke when the robot crosses, and
 * that is safe rather than lazy: the exit-side DoorClose phase
 * completes on supervisor-session ABSENCE and never reads
 * `door_states` at all, so a latched override provably cannot wedge a
 * robot after it has crossed. The revoke below is for ending one
 * early, not for unsticking anything.
 *
 * WHAT THIS PANEL CANNOT SHOW is written into `state/door-overrides.ts`
 * and repeated in the copy: there is no projection for an active
 * override, so the chip is this console's own record of what IT sent.
 * An override declared elsewhere shows nothing here.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StatusChip } from '../../design-system/components/StatusChip';
import { FormDialog } from '../../design-system/components/FormDialog';
import {
  DIALOG_FIELDSET,
  DIALOG_LEGEND,
  DIALOG_LIST,
  DIALOG_OPTION,
  DIALOG_OPTION_HINT,
  DIALOG_OPTION_LABEL,
} from '../../design-system/components/dialog-styles';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { DOOR_OVERRIDE_ROLES } from '../../auth/types';
import { useDeclareDoorOpen, useRevokeDoorOverride } from '../../api/hooks/useDoors';
import { useSafeSiteId } from '../../state/useActiveSite';
import { capabilitiesQueryOptions } from '../../api/query-options';
import { networkFailureText } from '../../api/v1-client';
import {
  forgetDoorOverride,
  grantedTtlS,
  rememberDoorOverride,
  remainingS,
  type DoorOverrideRecord,
} from '../../state/door-overrides';
import { useDoorOverrides } from '../../state/useDoorOverrides';
import { DOOR_OVERRIDE_CHECKLIST } from './door-override-checklist';
import {
  DOOR_OVERRIDE_DEFAULT_TTL_S,
  DOOR_OVERRIDE_MAX_TTL_S,
  type CommandRecord,
  type SiteId,
} from '../../api/v1';
import './DeviceDetailDrawers.css';

/** Offered TTLs. The top of the list IS the api-server's hard ceiling
 *  (`DOOR_OVERRIDE_MAX_TTL_S`), so nothing here can be clamped — the
 *  clamp is still handled on the response, because a stale SPA against
 *  a newer contract must not display a TTL it did not get. */
const TTL_CHOICES: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: DOOR_OVERRIDE_DEFAULT_TTL_S, label: '3 minutes (default)' },
  { seconds: 300, label: '5 minutes' },
  { seconds: 600, label: '10 minutes' },
  { seconds: DOOR_OVERRIDE_MAX_TTL_S, label: '30 minutes (the maximum)' },
];

const MAX_NOTE_LENGTH = 500;

function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The chip drawn beside a door's state while THIS console holds an
 *  override for it. Exported so the doors table draws the same one. */
export function DoorOverrideChip({ remaining }: { remaining: number }) {
  return (
    <span
      data-testid="door-override-chip"
      // One sentence for everyone, no "RMF" (review BC-F10, 2026-10-31):
      // the chip is drawn in the doors table too, which has no audience.
      title={
        'This console declared this door open by hand. The robots are told it is open, ' +
        'whatever the door reports.'
      }
    >
      <StatusChip status="warning" label={`OVERRIDE ${mmss(remaining)}`} />
    </span>
  );
}

function errorText(error: unknown): string {
  const blocked = networkFailureText(error);
  if (blocked) return blocked;
  return error instanceof Error && error.message ? error.message : 'The request failed.';
}

export function DoorOverridePanel({
  doorName,
  hasLiveState,
}: {
  doorName: string;
  /** Only used for the copy. The override is DELIBERATELY offered for
   *  a door nothing is publishing state for: that is the case it
   *  exists to rescue, and the mux synthesizes MODE_OPEN with the
   *  adapter dead. Everything else on this drawer disables itself for
   *  such a door, so the difference has to be said out loud or it
   *  reads as a bug. */
  hasLiveState: boolean;
}) {
  const siteId = useSafeSiteId();
  // A site supervisor holds this override too, so the panel splits its
  // engineering words (the mux, MODE_OPEN, the api-server's clamp) from
  // what they mean for robots (review BC-F10, 2026-10-31).
  const ops = audienceFor(useAuth().roles) === 'ops';
  const declare = useDeclareDoorOpen();
  const revoke = useRevokeDoorOverride();
  const { byDoor, now } = useDoorOverrides();
  const record = byDoor.get(doorName) ?? null;

  const capabilities = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const capabilityList = capabilities.data?.capabilities ?? [];
  const declareCap = capabilityList.find((c) => c.capability === 'door.declare_open');
  const revokeCap = capabilityList.find((c) => c.capability === 'door.revoke_override');
  const capabilitiesLoaded = capabilities.data !== undefined;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');
  const [ttlS, setTtlS] = useState<number>(DOOR_OVERRIDE_DEFAULT_TTL_S);

  // The panel is re-used IN PLACE when the door under it changes --
  // `DevicesScreen` tracks the selection by NAME and re-looks it up
  // every render, so the drawer's `door` prop changes without a
  // remount. Without this, a half-ticked checklist (and a mutation
  // result) written for one door would be sitting under another.
  useEffect(() => {
    setDialogOpen(false);
    setChecked({});
    setNote('');
    setTtlS(DOOR_OVERRIDE_DEFAULT_TTL_S);
    declare.reset();
    revoke.reset();
    // `declare`/`revoke` are stable mutation objects from react-query
    // but not referentially stable across renders; depending on them
    // would reset the form on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorName]);

  const allChecked = DOOR_OVERRIDE_CHECKLIST.every((item) => checked[item.field]);

  function send() {
    // Re-checked HERE and not only on the disabled button. The dialog
    // can sit open while state changes underneath it, and this is the
    // gate that matters: the api-server 422s a request with any box
    // missing or false, so a request built from a partial checklist is
    // not a weaker declaration, it is an error the operator cannot
    // read. Building it only when all three are true means the body
    // can never disagree with what was ticked.
    if (!allChecked) return;
    const trimmedNote = note.trim();
    const requested = ttlS;
    declare.mutate(
      {
        door: doorName,
        attestation: {
          i_can_see_the_door: true,
          the_door_is_fully_open: true,
          i_will_keep_it_open_until_the_robot_has_passed: true,
          ...(trimmedNote ? { note: trimmedNote } : {}),
        },
        ttlS: requested,
      },
      {
        onSuccess: (res) => {
          if (!siteId) return;
          // The GRANTED ttl, off the response -- never the one asked
          // for. The api-server clamps to 30 minutes rather than
          // refusing, so counting down from the request would show a
          // longer override than exists, and the mux enforces the real
          // one against wall clock regardless of what this says.
          const granted = grantedTtlS((res.command as CommandRecord).target) ?? requested;
          const entry: DoorOverrideRecord = {
            site: siteId,
            door: doorName,
            grantedAtMs: Date.now(),
            ttlS: granted,
            requestedTtlS: requested,
            commandId: res.command.command_id,
            note: trimmedNote || null,
          };
          rememberDoorOverride(entry);
        },
      },
    );
    setDialogOpen(false);
    setChecked({});
    setNote('');
  }

  function sendRevoke() {
    revoke.mutate(
      { door: doorName },
      {
        onSuccess: () => {
          if (siteId) forgetDoorOverride(siteId, doorName);
          // AND FORGET THE DECLARE'S RESULT. The "accepted for
          // delivery, nothing has confirmed it" line below is keyed on
          // `declare.data` alone, so without this it outlives the
          // override it describes: after a revoke the panel would draw
          // the Declare button with a banner underneath still saying a
          // declaration was accepted and to watch whether the robot
          // moves. On the one panel whose stated rule is that
          // "accepted" must never read as success, a stale banner
          // about a withdrawn declaration is the same mistake wearing
          // the opposite sign.
          declare.reset();
        },
      },
    );
  }

  const declareDisabled =
    declare.isPending || (capabilitiesLoaded && declareCap?.enabled === false);
  const revokeDisabled = revoke.isPending || (capabilitiesLoaded && revokeCap?.enabled === false);

  return (
    <RoleGuard
      allowedRoles={[...DOOR_OVERRIDE_ROLES]}
      fallback={
        record ? (
          <p className="nx-device-drawer__sub" data-testid="door-override-readonly">
            This door is under a manual-open override declared from this console. Ending it early
            takes a site supervisor or a platform administrator; otherwise it lapses on its own in{' '}
            {mmss(remainingS(record, now))}.
          </p>
        ) : null
      }
    >
      <section className="nx-device-drawer__override" aria-label="Manual-open override">
        {record ? (
          <>
            {/* NO chip here. The drawer already draws one beside the
                door's State, which is where it belongs -- the whole
                point of the chip is that "open" may be the override
                talking rather than the door. A second identical chip a
                few rows down would read as a second override. */}
            {ops ? (
              <p className="nx-device-drawer__sub" data-testid="door-override-active-note">
                RMF is being told {doorName} is open, whatever the door reports, for another{' '}
                {mmss(remainingS(record, now))}. It lapses by itself — the mux enforces the expiry
                against its own clock, so it ends even if nothing here is reachable.
                {record.ttlS < record.requestedTtlS && (
                  <> The {Math.round(record.requestedTtlS / 60)}-minute request was clamped to{' '}
                  {Math.round(record.ttlS / 60)} minutes by the api-server.</>
                )}
              </p>
            ) : (
              <p className="nx-device-drawer__sub" data-testid="door-override-active-note">
                The robots are told {doorName} is open, whatever the door reports, for another{' '}
                {mmss(remainingS(record, now))}. It ends by itself when the time runs out, even if
                this console can&apos;t reach the system.
                {record.ttlS < record.requestedTtlS && (
                  <> You asked for {Math.round(record.requestedTtlS / 60)} minutes; the most allowed
                  is {Math.round(record.ttlS / 60)}.</>
                )}
              </p>
            )}
            {record.note && (
              <p className="nx-device-drawer__sub" data-testid="door-override-note">
                Note on the record: “{record.note}”
              </p>
            )}
            <div className="nx-device-drawer__actions">
              <button
                type="button"
                data-testid="door-override-revoke-btn"
                disabled={revokeDisabled}
                title={
                  revokeCap?.enabled === false
                    ? revokeCap.disabled_reasons.join(', ')
                    : 'End the override now. The door goes back to reporting whatever it really is.'
                }
                // NO confirmation step, deliberately, and the same
                // asymmetry as the lift write gate's disarm: making it
                // harder to STOP asserting something than to start
                // would be backwards.
                onClick={sendRevoke}
              >
                {revoke.isPending ? 'Revoking…' : 'Revoke override'}
              </button>
              {revoke.isError && (
                <span className="nx-device-drawer__error" role="alert" data-testid="door-override-revoke-error">
                  {errorText(revoke.error)}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="nx-device-drawer__actions">
            <button
              type="button"
              data-testid="door-override-declare-btn"
              disabled={declareDisabled}
              title={
                declareCap?.enabled === false
                  ? declareCap.disabled_reasons.join(', ')
                  : 'Tell the robots this door is open so a waiting robot can cross.'
              }
              onClick={() => setDialogOpen(true)}
            >
              Declare manually open…
            </button>
            {declare.isError && (
              <span className="nx-device-drawer__error" role="alert" data-testid="door-override-error">
                {errorText(declare.error)}
              </span>
            )}
          </div>
        )}

        {/* ADMISSION IS NOT THE OUTCOME. `observed_applied` is the only
            state that means the worker got an answer back from the
            broker; everything short of it means the request left the
            building and nothing has come back, and the chip above is
            then this console's intention rather than a granted
            override. Said in words, because "accepted" is a lifecycle
            noun that reads as success. */}
        {declare.data && declare.data.command.state !== 'observed_applied' && (
          <p
            className="nx-device-drawer__sub"
            data-testid="door-override-unconfirmed"
            data-state={declare.data.command.state}
            role="status"
          >
            {ops
              ? 'The declaration was accepted for delivery but nothing has confirmed the broker wrote it.'
              : 'The declaration was sent, but nothing has confirmed it took.'}{' '}
            If the robot does not move within a few seconds, the override did not take —
            check the door&apos;s state above rather than treating this as done.
            {ops && declare.data.command.outcome_message ? ` ${declare.data.command.outcome_message}` : ''}
          </p>
        )}

        {/* The one thing this panel genuinely cannot know, said where
            it is read rather than buried in a tooltip. */}
        <p className="nx-device-drawer__sub" data-testid="door-override-scope-note">
          This console shows only the overrides it declared itself. Nothing publishes “this door
          is overridden”, so an override declared from another browser
          {ops ? ' or from the edge box' : ' or on site'} will not appear here.
        </p>
      </section>

      {dialogOpen && (
        <FormDialog
          title={`Declare ${doorName} manually open?`}
          confirmLabel="Declare open"
          variant="danger"
          confirmDisabled={!allChecked}
          onCancel={() => setDialogOpen(false)}
          onConfirm={send}
          testId="door-override-dialog"
          cancelTestId="door-override-dialog-cancel"
          confirmTestId="door-override-dialog-confirm"
        >
          <ul style={DIALOG_LIST}>
            {ops ? (
              <li>
                <strong>RMF will believe this door is open and will drive a robot through it.</strong>{' '}
                The door&apos;s own reports are suppressed for as long as the override lasts and
                MODE_OPEN is synthesized in their place — so nothing downstream can contradict this.
              </li>
            ) : (
              <li>
                <strong>Robots will treat this door as open and drive through it, whatever the door
                reports.</strong>{' '}
                Nothing the door says can change that until the override ends.
              </li>
            )}
            <li>
              Nothing is sent to the door. It does not open, close or move; if it is shut, it stays
              shut and the robot is driven at it. Its obstacle avoidance still applies.
            </li>
            <li>
              {ops
                ? 'It ends by itself when the time below runs out — enforced against the mux’s own clock, so it lapses even if this console is unreachable.'
                : 'It ends by itself when the time below runs out, even if this console can’t reach the system.'}{' '}
              You can also revoke it early.
            </li>
            {!hasLiveState && (
              <li data-testid="door-override-no-state-note">
                {ops ? 'Nothing is publishing state for' : 'Nothing is reporting the state of'} {doorName},
                so the Open and Close buttons above are disabled. This one is not:{' '}
                {ops
                  ? 'the override works with the door adapter dead, which is the situation it exists for.'
                  : 'the override works even when the door can’t be reached, which is the situation it exists for.'}
              </li>
            )}
          </ul>

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Your attestation — all three required</legend>
            {DOOR_OVERRIDE_CHECKLIST.map((item) => (
              <label key={item.field} style={DIALOG_OPTION}>
                <input
                  type="checkbox"
                  checked={Boolean(checked[item.field])}
                  onChange={(e) =>
                    setChecked((prev) => ({ ...prev, [item.field]: e.target.checked }))
                  }
                  data-testid={`door-override-ack-${item.field}`}
                />
                <span style={DIALOG_OPTION_LABEL}>{item.label}</span>
              </label>
            ))}
            <p style={DIALOG_OPTION_HINT}>
              These three go into the audit record as a statement in your name. Nothing checks them
              but you — the platform cannot see the door.
            </p>
          </fieldset>

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Expires after</legend>
            <select
              value={String(ttlS)}
              onChange={(e) => setTtlS(Number(e.target.value))}
              data-testid="door-override-ttl"
              aria-label="Expires after"
            >
              {TTL_CHOICES.map((choice) => (
                <option key={choice.seconds} value={String(choice.seconds)}>
                  {choice.label}
                </option>
              ))}
            </select>
            <p style={DIALOG_OPTION_HINT}>
              Long enough for a robot already standing at the door to cross. Declare it again if it
              lapses before the robot is through.
            </p>
          </fieldset>

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Note (optional, kept on the record)</legend>
            <input
              type="text"
              maxLength={MAX_NOTE_LENGTH}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="door-override-note-input"
              aria-label="Note"
              placeholder="e.g. propped with the fire extinguisher, engineer on site"
            />
          </fieldset>
        </FormDialog>
      )}
    </RoleGuard>
  );
}
